import { useContext, useState } from "react";
import cx from "classnames";
import { Link, useNavigate } from "react-router-dom";
import { t } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHouseUser,
  faUserFriends,
  faTools,
  faAnalytics,
  faEllipsisH,
  faTimes,
  faQuestionCircle,
  faCircleMinus,
  faCirclePlus,
} from "@fortawesome/pro-regular-svg-icons";
import { faThLarge } from "@fortawesome/pro-duotone-svg-icons";
import AppRoutes from "../../routes/AppRoutes";
import UserContext from "../../state/UserContext";
import NonPrimaryNavItems from "../../routes/NonPrimaryNavItems";
import { Badge } from "../common";
import { AnalyticsSubNavItems } from "../../routes/analytics";
import ttModulesList from "../../config/modules/ttModulesList";
import { moduleAccessHelper, userDetailsHelper } from "../../helpers";
import inAppSupportHelper from "../../helpers/inAppSupportHelper";
import permissionHelper from "../../helpers/permissionHelper";
import AdminSubNavItems from "../../routes/admin/AdminSubNavItems";
import { TouchNavItem } from "../../state/admin/TouchNavItem";
import adminHelper from "../../helpers/adminHelper";
import YourJourneySubNavItems from "../../routes/your-journey/YourJourneySubNavItems";
import YourPeopleSubNavItems from "../../routes/your-people/YourPeopleSubNavItems";

const TouchNav = () => {
  const [displayFullNav, setDisplayFullNav] = useState(false);
  const [expandedFullScreenNavItem, setExpandedFullScreenNavItem] = useState<
    string | null
  >(null);
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  let displayedNavItems = 1; // MyDashboard always displays

  const showInAppSupport = inAppSupportHelper.shouldShowForUser(
    userContext.user
  );

  /** Keeps a count of the currently displayed nav items, and
   * returns true or false depending on whether the number
   * of nav items displayed exceeds the maximum number allowed
   */
  const canDisplayNavItem = (boolValueToCheck: boolean | null) => {
    const maxNavItems = 4;
    const anotherNavItemWouldNotExceedMax =
      displayedNavItems + 1 <= maxNavItems;

    if (boolValueToCheck === null) {
      return anotherNavItemWouldNotExceedMax;
    }

    if (boolValueToCheck && anotherNavItemWouldNotExceedMax) {
      displayedNavItems += 1;
      return true;
    }
    return false;
  };

  const navigateAndCloseFullNav = (url: string) => {
    setDisplayFullNav(false);
    navigate(url);
  };

  const toggleExpandedNavItem = (groupName: string | null) => {
    const newNavItem =
      expandedFullScreenNavItem === groupName ? null : groupName;
    setExpandedFullScreenNavItem(newNavItem);
  };

  const handleHelpClick = () => {
    setDisplayFullNav(false);
    inAppSupportHelper.toggle();
  };

  const isInExitMode = userContext.user.myDashboardMode === "EXIT";

  const shouldShowAnalyticsNavItem =
    permissionHelper.shouldShowAnalyticsNavigation(userContext);

  const shouldShowCompanyDashboardNavItem = moduleAccessHelper.userHasModule(
    userContext.user,
    ttModulesList.CompanyDash
  );

  const shouldShowClassicAnalyticsNavItem =
    shouldShowAnalyticsNavItem &&
    moduleAccessHelper.userHasModule(userContext.user, ttModulesList.Reviews);

  const hasAiAnalytics = moduleAccessHelper.userHasModule(
    userContext.user,
    "AI Analytics"
  );

  const analyticsSubNavItems = AnalyticsSubNavItems(
    userContext.user.analyticsNavItems,
    shouldShowClassicAnalyticsNavItem,
    false,
    userContext.user.isAdmin,
    "TOUCH",
    hasAiAnalytics
  );

  const adminSubNavItems = AdminSubNavItems(userContext, "TOUCH");
  const shouldShowAdminNavItem =
    adminSubNavItems.length > 0 ||
    adminHelper.hasVisibleAdminDashboardCards(userContext);

  const yourJourneySubNavItems = YourJourneySubNavItems(userContext.user);
  const yourPeopleSubNavItems = YourPeopleSubNavItems(userContext.user);

  const managerBadgeNumber = userDetailsHelper.getManagerUpdateCount(
    userContext.user
  );

  const myDashboardBadgeNumber = userDetailsHelper.getOwnUpdateCount(
    userContext.user
  );

  let shouldShowYourPeopleSubNavItems =
    userContext.user.isManager || userContext.user.isJourneyManager;

  const helpNavItem: TouchNavItem = showInAppSupport
    ? {
        text: "Nav.Touch.Help",
        onClick: handleHelpClick,
      }
    : {
        text: "Nav.Touch.Help",
        href: AppRoutes.help,
      };

  let otherNavItems: TouchNavItem[] = [helpNavItem];

  if (yourJourneySubNavItems.length > 0) {
    otherNavItems = otherNavItems.concat(
      yourJourneySubNavItems.map((x) => {
        return { prefixText: "Your Journey", text: x.title, href: x.url };
      })
    );
  }

  if (
    shouldShowYourPeopleSubNavItems &&
    userContext.user.client.taskTypes &&
    userContext.user.client.taskTypes.length > 0
  ) {
    otherNavItems = otherNavItems.concat(
      yourPeopleSubNavItems.map((x) => {
        return { prefixText: "Team", text: x.title, href: x.url };
      })
    );
  }

  if (!isInExitMode && shouldShowAdminNavItem) {
    otherNavItems = otherNavItems.concat(
      adminSubNavItems.map((x) => {
        return { prefixText: "Admin", text: x.title, href: x.url };
      })
    );
  }

  if (shouldShowAnalyticsNavItem) {
    otherNavItems = otherNavItems.concat(
      analyticsSubNavItems.map((x) => {
        return { prefixText: "Analytics", text: x.title, href: x.url };
      })
    );
  }

  const ungroupedNavItems = otherNavItems.filter((item) => !item.prefixText);

  const groupedNavItems = otherNavItems.reduce(
    (acc: any, item: TouchNavItem) => {
      const { prefixText } = item;
      if (prefixText) {
        if (!acc[prefixText]) {
          acc[prefixText] = [];
        }
        acc[prefixText].push(item);
      }
      return acc;
    },
    {}
  );

  const groupedNavItemsArray = Object.keys(groupedNavItems).map((key) => ({
    prefixText: key,
    items: groupedNavItems[key],
  }));

  return (
    <>
      <div className="flex flex-row items-stretch justify-around border-t border-gray-300 pb-2">
        <div className="touch-nav-item">
          <Link to={AppRoutes.yourJourney.root}>
            <FontAwesomeIcon icon={faHouseUser} size="lg" />
            <span>{t("Nav.Touch.MyDashboard")}</span>
            {myDashboardBadgeNumber > 0 && (
              <Badge
                text={myDashboardBadgeNumber.toString()}
                backgroundColourClassName="accent-bg"
                textColourClassName="text-white"
                marginClassName="ml-2"
              />
            )}
          </Link>
        </div>
        <>
          {canDisplayNavItem(
            userContext.user.isManager || userContext.user.isJourneyManager
          ) && (
            <div className="touch-nav-item with-badge">
              <Link to={AppRoutes.yourPeople.root}>
                <FontAwesomeIcon icon={faUserFriends} size="lg" />
                <span>{t("Nav.Touch.ManagerDashboard")}</span>
                {managerBadgeNumber > 0 && (
                  <Badge
                    text={managerBadgeNumber.toString()}
                    backgroundColourClassName="accent-bg"
                    textColourClassName="text-white"
                    marginClassName="ml-2"
                  />
                )}
              </Link>
            </div>
          )}
          {canDisplayNavItem(shouldShowCompanyDashboardNavItem) && (
            <div className="touch-nav-item">
              <Link to={AppRoutes.companyDashboard}>
                <FontAwesomeIcon icon={faThLarge} size="lg" />
                <span>{t("Nav.Touch.CompanyDashboard")}</span>
              </Link>
            </div>
          )}

          {!isInExitMode && canDisplayNavItem(shouldShowAdminNavItem) && (
            <div className="touch-nav-item">
              <Link to={AppRoutes.admin.journeyManagement.path}>
                <FontAwesomeIcon icon={faTools} size="lg" />
                <span>{t("Nav.Touch.Admin.Root")}</span>
              </Link>
            </div>
          )}
          {canDisplayNavItem(shouldShowAnalyticsNavItem) && (
            <div className="touch-nav-item">
              <Link to={AppRoutes.analytics.usage}>
                <FontAwesomeIcon icon={faAnalytics} size="lg" />
                <span>{t("Nav.Touch.Analytics.Root")}</span>
              </Link>
            </div>
          )}
        </>
        {canDisplayNavItem(null) && (
          <div className="touch-nav-item">
            {showInAppSupport ? (
              <button type="button" onClick={() => inAppSupportHelper.toggle()}>
                <FontAwesomeIcon icon={faQuestionCircle} size="lg" />
                <span>{t("Nav.Touch.Help")}</span>
              </button>
            ) : (
              <a
                href={AppRoutes.help}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faQuestionCircle} size="lg" />
                <span>{t("Nav.Touch.Help")}</span>
              </a>
            )}
          </div>
        )}
        <div className="touch-nav-item">
          <button onClick={() => setDisplayFullNav(true)}>
            <FontAwesomeIcon icon={faEllipsisH} size="lg" />
            <span>{t("Nav.Touch.ExpandNav")}</span>
          </button>
        </div>
      </div>
      {displayFullNav && (
        <div className="h-screen w-screen fixed top-0 left-0 bg-white">
          <div className="flex flex-col h-full">
            <div className="flex-none h-12">
              <div className="relative">
                <button
                  className="absolute top-3 right-3 modal-close-icon"
                  onClick={() => setDisplayFullNav(false)}
                >
                  <FontAwesomeIcon icon={faTimes} size="2x" />
                </button>
              </div>
            </div>
            <div className="flex-grow text-center overflow-y-auto">
              <div className="h-full w-full flex flex-col justify-center items-center gap-5 font-medium">
                {ungroupedNavItems.map((item) => (
                  <FullScreenTouchNavLink
                    key={item.text}
                    item={item}
                    navigateAndCloseFullNav={navigateAndCloseFullNav}
                  />
                ))}

                {groupedNavItemsArray.map((group) => {
                  const isExpanded =
                    expandedFullScreenNavItem === group.prefixText;
                  return (
                    <div className="w-full" key={group.prefixText}>
                      <div
                        className={cx(
                          "mx-4",
                          isExpanded ? "bg-gray-100 pt-2 rounded-t-md" : ""
                        )}
                      >
                        <button
                          onClick={() =>
                            toggleExpandedNavItem(group.prefixText)
                          }
                        >
                          <span
                            className={cx(
                              "mr-2",
                              isExpanded ? "font-bold" : ""
                            )}
                          >
                            {group.prefixText}
                          </span>
                          <FontAwesomeIcon
                            icon={isExpanded ? faCircleMinus : faCirclePlus}
                            size="sm"
                            className="primary-color"
                          />
                        </button>
                      </div>
                      {isExpanded && (
                        <div className="bg-gray-100 rounded-b-md p-2 mx-4">
                          {group.items.map((item: TouchNavItem) => (
                            <div
                              className="mb-2 last:mb-0"
                              key={item.href || item.text}
                            >
                              <FullScreenTouchNavLink
                                item={item}
                                navigateAndCloseFullNav={
                                  navigateAndCloseFullNav
                                }
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {NonPrimaryNavItems.map((nonPrimaryNavItem) => (
                  <FullScreenTouchNavLink
                    key={nonPrimaryNavItem.href}
                    item={nonPrimaryNavItem}
                    navigateAndCloseFullNav={navigateAndCloseFullNav}
                  />
                ))}
              </div>
            </div>
            <div className="flex-none h-20 bg-white">
              <div className="flex flex-cols h-full items-end">
                <div className="flex-1 text-center bg-gray-100/60 h-16">
                  <div className="h-full w-full flex flex-col justify-center items-center">
                    &copy; {userContext.user.client.productName}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const FullScreenTouchNavLink = ({
  item,
  navigateAndCloseFullNav,
}: {
  item: TouchNavItem;
  navigateAndCloseFullNav: (href: string) => void;
}) => {
  if (item.onClick) {
    return (
      <div>
        <button type="button" onClick={item.onClick}>
          {t(item.text)}
        </button>
      </div>
    );
  }

  if (item.href?.startsWith("http")) {
    return (
      <div>
        <a href={item.href} target="_blank" rel="noopener noreferrer">
          {t(item.text)}
        </a>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => item.href && navigateAndCloseFullNav(item.href)}
      >
        {t(item.text)}
      </button>
    </div>
  );
};

export default TouchNav;
